import { Box, Stack } from '@mui/joy';
import { Divider } from '@mui/material';
import { FilterModel, IStatusPanelParams } from 'ag-grid-community';
import { sum, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ICompanySettings } from 'auth/types';
import { useAgGridClientTableContext } from 'core/states';
import { isEmptyObject } from 'core/utils';
import { QuoteEntityType, QuoteType, QuoteVersionType } from 'quotes/enums';
import { useGetQuoteVersionData } from 'quotes/hooks';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart } from 'quotes/types';

import { FreightRow } from './Freight';
import { getTotalFreight } from './Freight/utils';
import { fullWidthSxProps } from './style';
import { TotalAndFilterCount } from './TotalAndFilterCount';
import { TotalPricing } from './TotalPricing';
import { ITotalPricing } from './types';
import { Warnings } from './Warnings';

interface IProps extends IStatusPanelParams {
  entityType: string;
  quoteId: number;
  quoteVersionType?: QuoteVersionType;
  isDataLoadCompleted: boolean;
  companySettings: ICompanySettings;
  freightEnabled: boolean;
  quoteEntityType: QuoteEntityType;
  quoteType: QuoteType;
  onFreightChanged?: () => void;
  quote?: ICustomerQuote;
  onUpdateQuote?: (quote: ICustomerQuote) => void;
}

export const StatusBar = ({
  entityType,
  quoteId,
  quoteVersionType,
  isDataLoadCompleted,
  companySettings = {},
  freightEnabled = false,
  api,
  quoteEntityType = QuoteEntityType.QUOTE,
  quoteType = QuoteType.CUSTOMER,
  onFreightChanged,
  quote,
  onUpdateQuote,
}: IProps) => {
  const [activeRowData, setActiveRowData] = useState<IQuotePart[]>([]);
  const [filteredRowData, setFilteredRowData] = useState<IQuotePart[]>([]);
  const [filterModel, setFilterModel] = useState<FilterModel | null>(null);
  const [totals, setTotalPrices] = useState<ITotalPricing>({
    cost: undefined,
    price: undefined,
    margin: undefined,
    freight: undefined,
    total: undefined,
  });

  const { rows } = useAgGridClientTableContext();

  const { quoteVersionData, isLoading } = useGetQuoteVersionData(
    { quoteId, quoteVersionType },
    { enabled: isDataLoadCompleted }
  );

  const partsTotal = useMemo(() => totals.price, [totals]);

  const isFreightEditable = useMemo(
    () =>
      freightEnabled &&
      QuoteType.CUSTOMER === quoteType &&
      QuoteVersionType.DRAFT === quoteVersionType,
    [quoteVersionType, quoteType, freightEnabled]
  );

  const calculateTotalPrices = useCallback(
    (records: IQuotePart[]) => {
      const masterQuotePartIds = uniq(records.map((record) => record.masterQuotePartId));
      const activeRecordList = records.filter(
        (record) => !record.deleted && !record.noQuote && !masterQuotePartIds.includes(record.id)
      );

      const cost =
        sum(activeRecordList.map((record) => record.quantity * record.cost)) || undefined;
      const price =
        sum(activeRecordList.map((record) => record.quantity * record.price)) || undefined;
      const margin = price ? 1 - cost / price : undefined;
      const total =
        freightEnabled && totals.freight ? Number(price) + Number(totals.freight) : Number(price);

      setActiveRowData(activeRecordList);
      setTotalPrices((prev) => ({
        cost,
        price,
        margin,
        total,
        freight: prev.freight,
      }));
    },
    [freightEnabled, totals.freight]
  );

  const setTotalFreight = useCallback(
    (freight: number) => setTotalPrices((prev) => ({ ...prev, freight })),
    []
  );

  useEffect(() => {
    if (isEmptyObject(filterModel)) {
      setFilteredRowData([]);

      return;
    }

    const getFilteredRowData = () => {
      const filteredRowData = [];
      api.forEachNodeAfterFilter((node) => {
        node.data && filteredRowData.push(node.data);
      });

      return filteredRowData;
    };

    setFilteredRowData(getFilteredRowData());
  }, [api, filterModel]);

  useEffect(() => {
    const getFilterModel = () => {
      setFilterModel(api.getFilterModel());
    };

    api.addEventListener('filterChanged', getFilterModel);

    return () => {
      !api?.isDestroyed() && api.removeEventListener('filterChanged', getFilterModel);
    };
  }, [api]);

  useEffect(() => {
    calculateTotalPrices(rows as ICustomerQuotePart[]);
  }, [calculateTotalPrices, rows]);

  useEffect(() => {
    if (quoteVersionData && setTotalFreight && freightEnabled) {
      setTotalFreight(getTotalFreight(quoteVersionData));
    }
  }, [partsTotal, quoteVersionData, setTotalFreight, freightEnabled]);

  const classNames = 'ag-status-name-value ag-status-panel';

  return (
    <Stack spacing={0} sx={fullWidthSxProps}>
      {QuoteType.CUSTOMER === quoteType && freightEnabled && (
        <>
          <FreightRow
            quoteVersionData={quoteVersionData}
            freightSettings={companySettings}
            partsTotal={partsTotal}
            classNames={classNames}
            isLoading={isLoading}
            quoteEntityType={quoteEntityType}
            isEditable={isFreightEditable}
            onSubmitSuccess={onFreightChanged}
          />
          <Divider />
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <TotalPricing
            totals={totals}
            quoteVersionData={quoteVersionData}
            freightSettings={companySettings}
            classNames={classNames}
            freightEnabled={freightEnabled}
            isFreightEditable={isFreightEditable}
            quoteType={quoteType}
          />
          <Warnings
            records={activeRowData}
            quoteEntityType={quoteEntityType}
            classNames={classNames}
            quote={quote}
            onUpdateQuote={onUpdateQuote}
          />
        </Box>
        <TotalAndFilterCount
          entityType={entityType}
          count={rows.length}
          filteredCount={filteredRowData.length}
          classNames={classNames}
          showFiltered={!isEmptyObject(filterModel)}
        />
      </Box>
    </Stack>
  );
};
